import {
  setMetricsServerState,
  useGetOnlineModeStatusQuery,
  useLazyGetSeatConfigurationQuery,
  useSetOnlineModeStatusMutation
} from '@frontend/core/lib/domains/metrics_server'
import { MetricsServerNotification } from '@frontend/core/lib/domains/metrics_server/types'
import {
  getBlankSession,
  setSetupSession
} from '@frontend/core/lib/domains/sessions'
import { setSelectedSportType } from '@frontend/core/lib/domains/sports/slice'
import { upsertUploadStatus } from '@frontend/core/lib/domains/upload_status'
import {
  fetchServerConfig,
  fetchServerVersion,
  setServersConnected
} from '@frontend/core/lib/modules/servers'
import { updateLaptopContext } from '@frontend/core/lib/sentry'
import { WebSocketController } from '@frontend/core/lib/utils/websocket'
import { toast } from '@frontend/ui'
import { useCallback, useEffect } from 'react'
import {
  selectMetricsServerConnection,
  selectMetricsServerWebsocketUrl
} from '../../modules/servers'
import { useAppDispatch, useAppSelector } from '../../store/hooks'

let disconnectToastShown = false

export const SportableMetricsServer = () => {
  const dispatch = useAppDispatch()

  const [getSeatConfiguration] = useLazyGetSeatConfigurationQuery()

  const websocketUrl = useAppSelector(selectMetricsServerWebsocketUrl)
  const metricsServerConnection = useAppSelector(selectMetricsServerConnection)
  const { metricsServerState } = useAppSelector((state) => state.metricsServer)
  const state = useAppSelector((state) => state)

  const { laptop } = useAppSelector(
    (state) => state.metricsServer.metricsServerState.authState
  )

  const selectedSportType = useAppSelector(
    (state) => state.sports.selectedSportType
  )

  const currentSessionSetup = useAppSelector(
    (state) => state.sessions.setupSession
  )

  // Update Sentry laptop context when enrollment status or metrics server state changes //
  useEffect(() => {
    if (laptop) {
      updateLaptopContext(laptop, metricsServerState)
    }
  }, [metricsServerState, laptop])

  useEffect(() => {
    if (metricsServerConnection?.connected) {
      dispatch(fetchServerConfig('metricsServer'))
      dispatch(fetchServerVersion('metricsServer'))
    }
  }, [metricsServerConnection?.connected, dispatch])

  useEffect(() => {
    const initialAppSetup = metricsServerState.config.InitialAppSetup
    if (initialAppSetup) {
      const isSelectedSportTypeMisalignedWithAppSetup =
        selectedSportType != initialAppSetup.sportType

      if (isSelectedSportTypeMisalignedWithAppSetup) {
        dispatch(setSelectedSportType(initialAppSetup.sportType))
      }

      const isSessionSetupMisalignedWithAppSetup =
        currentSessionSetup.sport !== initialAppSetup.sportType ||
        currentSessionSetup.type !== initialAppSetup.sessionType ||
        currentSessionSetup.subType !== initialAppSetup.sessionSubType

      if (isSessionSetupMisalignedWithAppSetup) {
        dispatch(setSetupSession(getBlankSession(initialAppSetup)))
      }
    }
  }, [metricsServerState.config])

  useEffect(() => {
    if (!websocketUrl) {
      console.log('❌ No metrics server WebSocket URL available')
      return
    }

    // Use the existing WebSocketController (same pattern as other endpoints)
    WebSocketController.connectWebSocket(
      websocketUrl,
      'notifications',
      (data: MetricsServerNotification) => {
        switch (data.type) {
          case 'UploadStatus':
            dispatch(upsertUploadStatus(data.data))
            break
          case 'Authentication':
            dispatch(setMetricsServerState({ authState: data.authState }))
            break
          case 'OnlineProbe':
            dispatch(
              setMetricsServerState({ connectionState: data.connectionState })
            )
            break
          case 'SeatConfiguration':
            dispatch(
              setMetricsServerState({
                seatConfiguration: data.seatConfiguration
              })
            )
            break
          case 'Everything':
            dispatch(setMetricsServerState(data))
            break
          default:
            console.error('unhandled notification type: ', data)
            break
        }
      },
      'system-notifications',
      async () => {
        console.log('✅ Connected to metrics server WebSocket')
        dispatch(
          setServersConnected({
            key: 'metricsServer',
            connected: true
          })
        )
        disconnectToastShown = false
        if (metricsServerConnection?.hasMadeSuccessfulConnection) {
          toast.success('Metrics Server Connected', {
            description:
              'Successfully connected to the metrics server WebSocket.'
          })
        }

        // Fetch seat configuration on initial connection
        getSeatConfiguration()
      },
      () => {
        dispatch(
          setServersConnected({
            key: 'metricsServer',
            connected: false
          })
        )
        if (!disconnectToastShown) {
          toast.error('Metrics Server Connection Error', {
            description: 'Failed to connect to the metrics server WebSocket.'
          })
          disconnectToastShown = true
        }
        // Handle error case if needed
      }
    )

    return () => {
      console.log('🛑 useSystemNotifications hook unmounting')
      WebSocketController.disconnectWebSocket(
        'notifications',
        'system-notifications'
      )
      console.log('🔌 Disconnected from system notifications WebSocket')
    }
  }, [websocketUrl])

  // Show success message when connection is restored
  useEffect(() => {
    if (metricsServerState.connectionState === 'Online') {
      toast.success('Connection restored', {
        description: metricsServerState.msg || 'System is back online',
        duration: 5000
      })
    }
  }, [metricsServerState.connectionState])

  return null
}

export const SportableMetricsServerOfflineMode = () => {
  const dispatch = useAppDispatch()
  const { metricsServerState } = useAppSelector((state) => state.metricsServer)
  const { data: isManualOnlineMode } = useGetOnlineModeStatusQuery(undefined)
  const [setOnlineModeStatus] = useSetOnlineModeStatusMutation()

  const handleKeepTrying = useCallback(() => {
    // User chooses to keep trying online mode
    setOnlineModeStatus(false)
    toast.success('Continuing to attempt online connection...')
  }, [dispatch])

  const handleSwitchToOffline = useCallback(() => {
    // User chooses to switch to offline mode
    setOnlineModeStatus(true)
    toast.info('Switched to offline mode - using cached data')
  }, [dispatch])

  // Show notification when system goes offline but user hasn't manually chosen offline mode
  useEffect(() => {
    let timeout = null

    if (
      metricsServerState.connectionState &&
      metricsServerState.connectionState !== 'Online' &&
      isManualOnlineMode
    ) {
      timeout = setTimeout(() => {
        toast.warning('Connection Issues Detected', {
          description: metricsServerState.msg || 'Connection problems detected',
          action: {
            label: 'Go Offline',
            onClick: () => {
              console.log('Switching to offline mode')
              handleSwitchToOffline()
            }
          }
        })
      }, 10000)
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout)
      }
    }
  }, [
    metricsServerState.connectionState,
    isManualOnlineMode,
    handleKeepTrying,
    handleSwitchToOffline
  ])

  return null
}

// MAKE A list of things that need to happen when the ms disconnects and reconnects //

// - Reconnect to WebSocket
// - Reconnect to brokers and resubscriber to topics
// - Fetch server config and version
// - Update UI state to reflect connection status
// - Show notifications for connection status changes
